/**
 * Skill loader tool — fetches a markdown skill body so the model can follow
 * it as expert instructions for the current task. Skills are prompts, not
 * tools: any data mutations they describe still go through the registered
 * write tools and their confirmation flow.
 */

import { Schema } from "firebase/ai";
import { getSkill } from "@amazing-wedding/planner-skills";
import { resolveLocalizedSkillString } from "@amazing-wedding/shared-types";
import type { PlannerTool } from "./registry";
import { requireString } from "./helpers";

export const LOAD_SKILL_TOOL = "load_skill";

const SKILL_LOADING_LABEL: Record<"pl" | "en" | "de", string> = {
  pl: "Wczytuję wskazówki",
  en: "Loading skill",
  de: "Lade Skill",
};

const SKILL_UNKNOWN_LABEL: Record<"pl" | "en" | "de", string> = {
  pl: "nieznane",
  en: "unknown",
  de: "unbekannt",
};

export const skillTools: PlannerTool[] = [
  {
    declaration: {
      name: LOAD_SKILL_TOOL,
      description:
        "Load a skill — a focused playbook for one task such as reviewing a venue contract or a catering offer. Call this when the user's request matches a skill's whenToUse summary in the system instruction's Skills section. Returns the skill's title and body; follow the body's instructions for the rest of the turn.",
      parameters: Schema.object({
        properties: {
          skillId: Schema.string({
            description:
              "The kebab-case id of the skill to load, taken from the Skills section of the system instruction.",
          }),
        },
      }),
    },
    destructive: false,
    describe: (args, ctx) => {
      const id = typeof args.skillId === "string" ? args.skillId : "";
      const lang = ctx.wedding().settings.language;
      const skill = id ? getSkill(id) : null;
      const loadingLabel = SKILL_LOADING_LABEL[lang] ?? SKILL_LOADING_LABEL.en;
      if (!skill) {
        const fallback = SKILL_UNKNOWN_LABEL[lang] ?? SKILL_UNKNOWN_LABEL.en;
        return `${loadingLabel}: ${id || fallback}`;
      }
      const title = resolveLocalizedSkillString(skill.frontmatter.title, lang);
      return `${loadingLabel}: ${title}`;
    },
    execute: async (args, ctx) => {
      const id = requireString(args, "skillId");
      const skill = getSkill(id);
      if (!skill) {
        throw new Error(
          `Unknown skill id "${id}". Use one of the ids listed in the system prompt.`,
        );
      }
      const lang = ctx.wedding().settings.language;
      return {
        skillId: skill.frontmatter.id,
        title: resolveLocalizedSkillString(skill.frontmatter.title, lang),
        body: skill.body,
      };
    },
  },
];
